import { useState, useEffect, useCallback, useRef } from "react";
import {
    Cpu,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    Calendar,
    Filter,
    TrendingUp,
    Box,
    Activity,
    Zap,
    RefreshCw,
    X,
    Layers,
    ExternalLink,
    Download,
    FileSpreadsheet,
    FileJson,
    FileText,
    FileDown,
    ChevronDown,
} from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
} from "recharts";
import jsPDF from "jspdf";
import apiClient from "../../../api/api";
import { API_ENDPOINTS } from "../../../api/api.config";

interface NodeBreakdown {
    node_name: string;
    model_name: string;
    node_type: string;
    total_tokens: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    usage_count: number;
}

interface ModelBreakdown {
    model_name: string;
    total_tokens: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    usage_count: number;
}

interface NodeTypeBreakdown {
    node_type: string;
    model_name: string;
    total_tokens: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    usage_count: number;
}

interface TemplateTokenData {
    total_executions: number;
    total_tokens: number;
    total_prompt_tokens: number;
    total_completion_tokens: number;
    avg_tokens_per_execution: number;
    model_breakdown: ModelBreakdown[];
    node_breakdown: NodeBreakdown[];
    node_type_breakdown: NodeTypeBreakdown[];
}

const CHART_COLORS = [
    "#2563EB",
    "#0891B2",
    "#059669",
    "#D97706",
    "#DC2626",
    "#7C3AED",
    "#4F46E5",
    "#0D9488",
];

const NODE_TYPE_NAMES: Record<string, string> = {
    "GEMINI-UNDERSTANDING": "Smart Extraction",
    "SMART-PARSE": "Document Parsing",
    "SUMMARY": "LLM Node",
    "FILE-OCR": "OCR Extraction",
    "CLOUD-VISION": "Cloud OCR",
    "CODE": "Code Node",
};

const formatTokenCount = (count: number): string => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(2)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toLocaleString();
};

const formatDate = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
};

const toInputDate = (date: Date): string => date.toISOString().split("T")[0];

interface Props {
    templateId: string;
    workflowName?: string;
    onClose: () => void;
}

const WorkflowTokenAnalytics = ({ templateId, workflowName, onClose }: Props) => {
    const [data, setData] = useState<TemplateTokenData | null>(null);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState<string>(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return toInputDate(d);
    });
    const [toDate, setToDate] = useState<string>(() => toInputDate(new Date()));

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const from = formatDate(new Date(fromDate));
            const to = formatDate(new Date(toDate));
            const response = await apiClient.post(
                API_ENDPOINTS.WORKFLOW.TOKEN_ANALYSIS_BY_TEMPLATE,
                { template_id: templateId, from_date: from, to_date: to }
            );
            if (response.data?.success && response.data?.data) {
                setData(response.data.data);
            }
        } catch (err) {
            console.error("Token analysis fetch failed:", err);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate, templateId]);

    useEffect(() => {
        fetchData();
    }, []);

    const pieData =
        data?.model_breakdown.map((m) => ({
            name: m.model_name,
            value: m.total_tokens,
            usage_count: m.usage_count,
        })) || [];

    const nodeBarData =
        data?.node_breakdown.map((n) => ({
            name: n.node_name,
            type: NODE_TYPE_NAMES[n.node_type] || n.node_type,
            model: n.model_name,
            prompt: n.total_prompt_tokens,
            completion: n.total_completion_tokens,
            total: n.total_tokens,
            count: n.usage_count,
        })) || [];

    const promptRatio = data && data.total_tokens > 0
        ? ((data.total_prompt_tokens / data.total_tokens) * 100).toFixed(1)
        : "0";
    const completionRatio = data && data.total_tokens > 0
        ? ((data.total_completion_tokens / data.total_tokens) * 100).toFixed(1)
        : "0";

    const [showDownloadMenu, setShowDownloadMenu] = useState(false);
    const downloadMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as HTMLElement)) {
                setShowDownloadMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const triggerDownload = (content: string, filename: string, mimeType: string) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setShowDownloadMenu(false);
    };

    const downloadCSV = () => {
        if (!data) return;
        const lines: string[] = [];
        lines.push("Token Usage Report");
        lines.push(`Workflow,${workflowName || "N/A"}`);
        lines.push(`Date Range,${fromDate} to ${toDate}`);
        lines.push(`Generated,${new Date().toLocaleString()}`);
        lines.push("");
        lines.push("SUMMARY");
        lines.push("Metric,Value");
        lines.push(`Total Executions,${data.total_executions}`);
        lines.push(`Total Tokens,${data.total_tokens}`);
        lines.push(`Total Prompt Tokens,${data.total_prompt_tokens}`);
        lines.push(`Total Completion Tokens,${data.total_completion_tokens}`);
        lines.push(`Avg Tokens Per Execution,${data.avg_tokens_per_execution}`);
        lines.push("");
        lines.push("MODEL BREAKDOWN");
        lines.push("Model,Total Tokens,Prompt Tokens,Completion Tokens,Usage Count");
        data.model_breakdown.forEach((m) => {
            lines.push(`${m.model_name},${m.total_tokens},${m.total_prompt_tokens},${m.total_completion_tokens},${m.usage_count}`);
        });
        lines.push("");
        lines.push("NODE TYPE BREAKDOWN");
        lines.push("Node Type,Model,Total Tokens,Prompt Tokens,Completion Tokens,Usage Count");
        data.node_type_breakdown.forEach((n) => {
            lines.push(`${n.node_type},${n.model_name},${n.total_tokens},${n.total_prompt_tokens},${n.total_completion_tokens},${n.usage_count}`);
        });
        const safeName = (workflowName || "workflow").replace(/[^a-zA-Z0-9]/g, "_");
        triggerDownload(lines.join("\n"), `token-report-${safeName}-${fromDate}-to-${toDate}.csv`, "text/csv;charset=utf-8;");
    };

    const downloadJSON = () => {
        if (!data) return;
        const { node_breakdown, ...restData } = data;
        const report = {
            report: "Token Usage Report",
            workflow: workflowName || "N/A",
            template_id: templateId,
            date_range: { from: fromDate, to: toDate },
            generated_at: new Date().toISOString(),
            ...restData,
        };
        const safeName = (workflowName || "workflow").replace(/[^a-zA-Z0-9]/g, "_");
        triggerDownload(JSON.stringify(report, null, 2), `token-report-${safeName}-${fromDate}-to-${toDate}.json`, "application/json");
    };

    const downloadText = () => {
        if (!data) return;
        const sep = "─".repeat(60);
        const lines: string[] = [];
        lines.push("TOKEN USAGE REPORT");
        lines.push(sep);
        lines.push(`Workflow:       ${workflowName || "N/A"}`);
        lines.push(`Date Range:     ${fromDate} to ${toDate}`);
        lines.push(`Generated:      ${new Date().toLocaleString()}`);
        lines.push("");
        lines.push("SUMMARY");
        lines.push(sep);
        lines.push(`Total Executions:          ${data.total_executions}`);
        lines.push(`Total Tokens:              ${data.total_tokens.toLocaleString()}`);
        lines.push(`  Prompt Tokens:           ${data.total_prompt_tokens.toLocaleString()}`);
        lines.push(`  Completion Tokens:       ${data.total_completion_tokens.toLocaleString()}`);
        lines.push(`Avg Tokens / Execution:    ${data.avg_tokens_per_execution.toLocaleString()}`);
        lines.push("");
        lines.push("MODEL BREAKDOWN");
        lines.push(sep);
        data.model_breakdown.forEach((m) => {
            lines.push(`  ${m.model_name}`);
            lines.push(`    Tokens: ${m.total_tokens.toLocaleString()} (Prompt: ${m.total_prompt_tokens.toLocaleString()}, Completion: ${m.total_completion_tokens.toLocaleString()})`);
            lines.push(`    Calls:  ${m.usage_count}`);
        });
        const safeName = (workflowName || "workflow").replace(/[^a-zA-Z0-9]/g, "_");
        triggerDownload(lines.join("\n"), `token-report-${safeName}-${fromDate}-to-${toDate}.txt`, "text/plain;charset=utf-8;");
    };

    const downloadPDF = () => {
        if (!data) return;
        setShowDownloadMenu(false);
        const doc = new jsPDF("p", "mm", "a4");
        const pageW = doc.internal.pageSize.getWidth();
        const margin = 16;
        const contentW = pageW - margin * 2;
        let y = 20;

        const checkPage = (needed: number) => {
            if (y + needed > doc.internal.pageSize.getHeight() - 15) {
                doc.addPage();
                y = 20;
            }
        };

        // Header bar
        doc.setFillColor(37, 99, 235);
        doc.rect(0, 0, pageW, 32, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Token Usage Report", margin, 14);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`${workflowName || "Workflow"} | ${fromDate} to ${toDate}`, margin, 22);
        doc.text(`Generated: ${new Date().toLocaleString()}`, margin, 28);

        y = 42;
        doc.setTextColor(30, 41, 59);

        // Summary section
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text("Summary", margin, y);
        y += 2;
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y, pageW - margin, y);
        y += 7;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const summaryItems = [
            ["Total Executions", data.total_executions.toLocaleString()],
            ["Total Tokens", data.total_tokens.toLocaleString()],
            ["Prompt Tokens", data.total_prompt_tokens.toLocaleString()],
            ["Completion Tokens", data.total_completion_tokens.toLocaleString()],
            ["Avg Tokens / Execution", data.avg_tokens_per_execution.toLocaleString()],
        ];

        // Summary cards as a row
        const cardW = contentW / summaryItems.length;
        summaryItems.forEach(([label, value], i) => {
            const cx = margin + i * cardW;
            doc.setFillColor(248, 250, 252);
            doc.roundedRect(cx + 1, y, cardW - 2, 18, 2, 2, "F");
            doc.setFontSize(7);
            doc.setTextColor(100, 116, 139);
            doc.text(label, cx + cardW / 2, y + 6, { align: "center" });
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(30, 41, 59);
            doc.text(value, cx + cardW / 2, y + 14, { align: "center" });
            doc.setFont("helvetica", "normal");
        });
        y += 26;

        // Model Breakdown
        checkPage(40);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(30, 41, 59);
        doc.text("Model Breakdown", margin, y);
        y += 2;
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y, pageW - margin, y);
        y += 6;

        // Table header
        doc.setFillColor(248, 250, 252);
        doc.rect(margin, y - 3, contentW, 8, "F");
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 116, 139);
        const mCols = [margin, margin + 55, margin + 90, margin + 120, margin + 150];
        doc.text("Model", mCols[0] + 2, y + 2);
        doc.text("Total Tokens", mCols[1], y + 2);
        doc.text("Prompt", mCols[2], y + 2);
        doc.text("Completion", mCols[3], y + 2);
        doc.text("Calls", mCols[4], y + 2);
        y += 8;

        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 65, 85);
        doc.setFontSize(8);
        data.model_breakdown.forEach((m) => {
            checkPage(8);
            doc.text(m.model_name, mCols[0] + 2, y);
            doc.text(m.total_tokens.toLocaleString(), mCols[1], y);
            doc.text(m.total_prompt_tokens.toLocaleString(), mCols[2], y);
            doc.text(m.total_completion_tokens.toLocaleString(), mCols[3], y);
            doc.text(String(m.usage_count), mCols[4], y);
            y += 6;
        });
        y += 6;

        // Footer
        const pageCount = doc.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(148, 163, 184);
            doc.text(`Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 8, { align: "center" });
        }

        const safeName = (workflowName || "workflow").replace(/[^a-zA-Z0-9]/g, "_");
        doc.save(`token-report-${safeName}-${fromDate}-to-${toDate}.pdf`);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex flex-col p-6">
            <div className="w-full h-full flex flex-col overflow-hidden rounded-2xl shadow-2xl">
                {/* Header */}
                <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
                            <Cpu className="h-4 w-4 text-white" />
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-slate-900">
                                {workflowName || "Workflow"}
                            </h2>
                            <p className="text-slate-500 text-xs">
                                Workflow level token usage analytics
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href="https://www.llm-prices.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            LLM Price Calculator
                        </a>
                        {/* Download Dropdown */}
                        {data && (
                            <div className="relative" ref={downloadMenuRef}>
                                <button
                                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                >
                                    <Download className="h-4 w-4" />
                                    <span>Export</span>
                                    <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${showDownloadMenu ? "rotate-180" : ""}`} />
                                </button>
                                {showDownloadMenu && (
                                    <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg z-20 py-1">
                                        <button
                                            onClick={downloadCSV}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                                <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium">CSV Report</p>
                                                <p className="text-[10px] text-slate-400">Spreadsheet format</p>
                                            </div>
                                        </button>
                                        <button
                                            onClick={downloadJSON}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                <FileJson className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium">JSON Report</p>
                                                <p className="text-[10px] text-slate-400">Structured data format</p>
                                            </div>
                                        </button>
                                        <button
                                            onClick={downloadText}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                <FileText className="h-4 w-4 text-slate-600" />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium">Text Report</p>
                                                <p className="text-[10px] text-slate-400">Plain text summary</p>
                                            </div>
                                        </button>
                                        <div className="border-t border-slate-100 my-1" />
                                        <button
                                            onClick={downloadPDF}
                                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center flex-shrink-0">
                                                <FileDown className="h-4 w-4 text-red-600" />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium">PDF Report</p>
                                                <p className="text-[10px] text-slate-400">Formatted document</p>
                                            </div>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-2 rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="bg-white flex-1 overflow-y-auto">
                    {/* Date Filter Bar */}
                    <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 sticky top-0 z-10">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Filter className="h-4 w-4" />
                                <span className="font-medium">Date Range:</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Calendar className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                    />
                                </div>
                                <span className="text-slate-400 text-xs">to</span>
                                <div className="relative">
                                    <Calendar className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={fetchData}
                                disabled={loading}
                                className="flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                                Apply
                            </button>
                            <div className="flex items-center gap-1 ml-auto">
                                {[
                                    { label: "Today", days: 0 },
                                    { label: "7D", days: 7 },
                                    { label: "30D", days: 30 },
                                    { label: "90D", days: 90 },
                                ].map((preset) => (
                                    <button
                                        key={preset.label}
                                        onClick={() => {
                                            const to = new Date();
                                            const from = new Date();
                                            from.setDate(from.getDate() - preset.days);
                                            setFromDate(toInputDate(from));
                                            setToDate(toInputDate(to));
                                        }}
                                        className="text-xs px-2.5 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {data && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                                    <Calendar className="h-3 w-3" />
                                    {new Date(fromDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                    {" "}&mdash;{" "}
                                    {new Date(toDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                </span>
                                <span className="text-slate-400">|</span>
                                <span>{data.total_executions} executions &middot; {data.total_tokens.toLocaleString("en-IN")} total tokens</span>
                            </div>
                        )}
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <RefreshCw className="h-6 w-6 text-blue-500 animate-spin" />
                            <span className="ml-2 text-sm text-slate-500">Loading analytics...</span>
                        </div>
                    )}

                    {!loading && data && (
                        <div className="p-6 space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                                            <Cpu className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Tokens</span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">{formatTokenCount(data.total_tokens)}</p>
                                    <p className="text-xs text-slate-400 mt-1">{data.total_tokens.toLocaleString()} tokens</p>
                                </div>

                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center">
                                            <ArrowUpRight className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Prompt</span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">{formatTokenCount(data.total_prompt_tokens)}</p>
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${promptRatio}%` }} />
                                        </div>
                                        <span className="text-xs text-slate-500">{promptRatio}%</span>
                                    </div>
                                </div>

                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-teal-50 flex items-center justify-center">
                                            <ArrowDownRight className="h-4 w-4 text-teal-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Completion</span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">{formatTokenCount(data.total_completion_tokens)}</p>
                                    <div className="flex items-center gap-1.5 mt-2">
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-teal-500 rounded-full" style={{ width: `${completionRatio}%` }} />
                                        </div>
                                        <span className="text-xs text-slate-500">{completionRatio}%</span>
                                    </div>
                                </div>

                                <div className="bg-white border border-slate-200 rounded-lg p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center">
                                            <TrendingUp className="h-4 w-4 text-amber-600" />
                                        </div>
                                        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg / Execution</span>
                                    </div>
                                    <p className="text-2xl font-bold text-slate-900">{formatTokenCount(data.avg_tokens_per_execution)}</p>
                                    <p className="text-xs text-slate-400 mt-1">{data.total_executions} total executions</p>
                                </div>
                            </div>

                            {/* Charts Row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Model Breakdown Donut */}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Layers className="h-4 w-4 text-blue-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">Token Usage by Model</h3>
                                    </div>
                                    {pieData.length > 0 ? (
                                        <div className="flex items-center">
                                            <div className="w-1/2 h-48">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={pieData}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={45}
                                                            outerRadius={75}
                                                            paddingAngle={3}
                                                            dataKey="value"
                                                            stroke="none"
                                                        >
                                                            {pieData.map((_, index) => (
                                                                <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip
                                                            formatter={(value: number) => formatTokenCount(value)}
                                                            contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            <div className="w-1/2 space-y-3">
                                                {data.model_breakdown.map((model, i) => (
                                                    <div key={model.model_name} className="flex items-start gap-2">
                                                        <div
                                                            className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0"
                                                            style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                                                        />
                                                        <div className="min-w-0">
                                                            <p className="text-xs font-medium text-slate-700 truncate">{model.model_name}</p>
                                                            <p className="text-xs text-slate-500">
                                                                {formatTokenCount(model.total_tokens)} tokens
                                                                <span className="text-slate-400"> ({model.usage_count} calls)</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-400 text-center py-8">No model data</p>
                                    )}
                                </div>

                                {/* Prompt vs Completion */}
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Activity className="h-4 w-4 text-slate-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">Prompt vs Completion</h3>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-1/2 h-48">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: "Prompt", value: data.total_prompt_tokens, color: "#2563EB" },
                                                            { name: "Completion", value: data.total_completion_tokens, color: "#0891B2" },
                                                        ].filter((d) => d.value > 0)}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={45}
                                                        outerRadius={75}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        stroke="none"
                                                    >
                                                        <Cell fill="#2563EB" />
                                                        <Cell fill="#0891B2" />
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value: number) => formatTokenCount(value)}
                                                        contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div className="w-1/2 space-y-4">
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                                                        <span className="text-xs font-medium text-slate-600">Prompt</span>
                                                    </div>
                                                    <span className="text-xs font-bold text-slate-800">{formatTokenCount(data.total_prompt_tokens)}</span>
                                                </div>
                                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${promptRatio}%` }} />
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-2.5 h-2.5 rounded-full bg-teal-600" />
                                                        <span className="text-xs font-medium text-slate-600">Completion</span>
                                                    </div>
                                                    <span className="text-xs font-bold text-slate-800">{formatTokenCount(data.total_completion_tokens)}</span>
                                                </div>
                                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full bg-teal-600 transition-all" style={{ width: `${completionRatio}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Per-Node Bar Chart */}
                            {nodeBarData.length > 0 && (
                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-4">
                                        <Box className="h-4 w-4 text-slate-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">Token Usage by Node</h3>
                                    </div>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={nodeBarData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} />
                                                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickFormatter={(v) => formatTokenCount(v)} />
                                                <RechartsTooltip
                                                    formatter={(value: number, name: string) => [
                                                        formatTokenCount(value),
                                                        name === "prompt" ? "Prompt Tokens" : "Completion Tokens",
                                                    ]}
                                                    contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }}
                                                />
                                                <Legend formatter={(value) => (value === "prompt" ? "Prompt" : "Completion")} iconSize={10} wrapperStyle={{ fontSize: "12px" }} />
                                                <Bar dataKey="prompt" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={60} />
                                                <Bar dataKey="completion" fill="#0891B2" radius={[4, 4, 0, 0]} maxBarSize={60} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            )}

                            {/* Node Breakdown Table */}
                            {data.node_breakdown && data.node_breakdown.length > 0 && (
                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                    <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                                        <Zap className="h-4 w-4 text-amber-500" />
                                        <h3 className="text-sm font-semibold text-slate-800">Node-Level Breakdown</h3>
                                        <span className="ml-auto text-xs text-slate-500">{data.node_breakdown.length} nodes</span>
                                    </div>

                                    {/* Table Header */}
                                    <div className="grid grid-cols-12 gap-2 px-5 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100 bg-white">
                                        <div className="col-span-3">Node Name</div>
                                        <div className="col-span-2">Type</div>
                                        <div className="col-span-2">Model</div>
                                        <div className="col-span-1 text-right">Calls</div>
                                        <div className="col-span-1 text-right">Prompt</div>
                                        <div className="col-span-1 text-right">Completion</div>
                                        <div className="col-span-2 text-right">Total</div>
                                    </div>

                                    {/* Rows */}
                                    <div className="divide-y divide-slate-50">
                                        {data.node_breakdown
                                            .sort((a, b) => b.total_tokens - a.total_tokens)
                                            .map((node, i) => {
                                                const maxTokens = Math.max(...data.node_breakdown.map((n) => n.total_tokens));
                                                const barWidth = maxTokens > 0 ? (node.total_tokens / maxTokens) * 100 : 0;
                                                return (
                                                    <div key={`${node.node_name}-${i}`}>
                                                        <div className="grid grid-cols-12 gap-2 px-5 py-3 items-center hover:bg-slate-50 transition-colors">
                                                            <div className="col-span-3 text-xs font-medium text-slate-700 truncate">
                                                                {node.node_name}
                                                            </div>
                                                            <div className="col-span-2">
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                                                                    {NODE_TYPE_NAMES[node.node_type] || node.node_type}
                                                                </span>
                                                            </div>
                                                            <div className="col-span-2 text-xs text-slate-500 truncate">
                                                                {node.model_name}
                                                            </div>
                                                            <div className="col-span-1 text-right text-xs text-slate-600">
                                                                {node.usage_count}
                                                            </div>
                                                            <div className="col-span-1 text-right text-xs text-slate-600">
                                                                {formatTokenCount(node.total_prompt_tokens)}
                                                            </div>
                                                            <div className="col-span-1 text-right text-xs text-slate-600">
                                                                {formatTokenCount(node.total_completion_tokens)}
                                                            </div>
                                                            <div className="col-span-2 text-right text-xs font-semibold text-slate-800">
                                                                {formatTokenCount(node.total_tokens)}
                                                            </div>
                                                        </div>
                                                        <div className="px-5 pb-2 -mt-1">
                                                            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                                                                <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${barWidth}%` }} />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && !data && (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <BarChart3 className="h-8 w-8 mb-2" />
                            <p className="text-sm">No analytics data available</p>
                            <p className="text-xs mt-1">Try adjusting the date range and click Apply</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkflowTokenAnalytics;
